import React, { useState, useMemo } from 'react';
import classnames from 'classnames';
import { useSize, useDeepCompareEffect } from 'ahooks';
import { Table, TableAddon, TableColumn } from 'tea-component';
import CollapsibleMultiTexts from '@gp/components/CollapsibleMultiTexts';
import { ActionItemStatusTag } from '@gp/components/StatusTag';
import useTableAutotipAddon from '@gp/hooks/useTableAutotipAddon';
import { isEmpty, formatDateStr } from '@gp/utils/common';
import { CommonColumnBuilders } from '@gp/utils/columns';
import { getProjectActionItemKey } from './utils';
import { ACTION_ITEM_STATUS_MAPPING } from '@gp/constants/const';
import RestrictedContent from '@gp/components/RestrictedContent';
import type { SortBy } from 'tea-component/lib/table/addons';
import type { IBaseProps } from '@home/types/common';
import type { IProjectGRActionItemProjectDTO, IProjectGRActionItemProjectVO } from '@gp/types/grActionItems';
import './index.scss';

interface IGRActionItemsTablesProps extends IBaseProps {
  loading?: boolean;
  error?: Error;
  isShowAll?: boolean;
  data?: any;
  sort?: SortBy<IProjectGRActionItemProjectVO>[];
  onSortChange?: (sort: SortBy<IProjectGRActionItemProjectVO>[]) => void;
  onRetry?: () => void;
  onReset?: () => void;
  onEdit?: (payload?: IProjectGRActionItemProjectDTO | null) => void;
}

const { sortable } = Table.addons;

const GRActionItemsTables: React.FC<IGRActionItemsTablesProps> = (props) => {
  const { className, loading = true, error, isShowAll = false, data, onRetry, onReset } = props;

  const { project_auth: projectAuth } = data || {};
  // const projectAuth = false;

  const [records, setRecords] = useState([]);

  // const { width } = useSize(document.body) || {};
  useDeepCompareEffect(() => {
    const tableList = data?.gr_action_items || [];

    const recordTemp = tableList?.map((item: any) => {
      const objTemp = {
        ...data,
        ...item,
      };
      delete objTemp.gr_action_items;
      return objTemp;
    });
    setRecords(recordTemp);
  }, [data]);
  const [sort, setSort] = useState([{ by: 'due_date', order: 'asc' }]);

  const autotipAddon = useTableAutotipAddon({
    isLoading: loading,
    isError: !!error,
    onRetry,
    isFound: !records?.length,
    onClear: onReset,
    isUseResetNodata: true,
  });

  const addons = useMemo<TableAddon<IProjectGRActionItemProjectVO>[]>(() => {
    return [
      sortable({
        columns: ['due_date'],
        value: sort,
        onChange: (payload: SortBy<IProjectGRActionItemProjectVO>[]) => {
          setSort(payload);
          const dateSort = payload?.find((item: any) => item.by === 'due_date')?.order || 'asc';
          // onSortChangeProps?.(payload);
          const listTemp = [...records];
          const sortedArray = listTemp?.sort((a: any, b: any) => {
            const dateA: any = new Date(a.due_date);
            const dateB: any = new Date(b.due_date);
            return dateSort === 'asc' ? dateA - dateB : dateB - dateA;
          });
          setRecords(sortedArray);
        },
      }),
    ];
  }, [loading, autotipAddon]);

  const columns: TableColumn<IProjectGRActionItemProjectVO>[] = [
    {
      key: 'category',
      header: 'Category',
      width: 122,
      render: (cvm: any) => {
        const { category } = cvm || {};
        if (isEmpty(category)) return <span>-</span>;
        return <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>{category}</div>;
      },
    },
    {
      key: 'action_content',
      header: 'Action Items',
      width: 229,
      render: (cvm) => {
        const { action_content: actionContent } = cvm || {};
        if (projectAuth === false) return <RestrictedContent />;
        return <CollapsibleMultiTexts isShowAll={isShowAll} text={actionContent} />;
      },
    },
    {
      key: 'user',
      header: 'Owner',
      width: 100,
      render: (cvm) => {
        const { user } = cvm || {};
        if (projectAuth === false) return <RestrictedContent />;
        if (isEmpty(user)) return <span>-</span>;
        return <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>{user}</div>;
      },
    },
    {
      key: 'due_date',
      header: 'Due Date',
      width: 138,
      render: (cvm) => {
        const { due_date: dueDate } = cvm || {};
        if (projectAuth === false) return <RestrictedContent />;
        const dueDateFormatted = formatDateStr(dueDate);
        return <span>{dueDateFormatted || '-'}</span>;
      },
    },
    {
      key: 'state',
      header: 'Status',
      width: 112,
      render: (cvm) => {
        const { state } = cvm || {};
        if (projectAuth === false) return <RestrictedContent />;
        if (isEmpty(state)) return <span>-</span>;
        const stateNum = ACTION_ITEM_STATUS_MAPPING[state?.toLowerCase()];
        return <ActionItemStatusTag status={stateNum} />;
      },
    },
    {
      key: 'state_comment',
      header: 'Status Comment',
      width: 210,
      render: (cvm) => {
        const { state_comment: stateComment } = cvm || {};
        if (projectAuth === false) return <RestrictedContent />;
        return <CollapsibleMultiTexts isShowAll={isShowAll} text={stateComment} />;
      },
    },
    // CommonColumnBuilders.buildLastUpdateColumn<IProjectGRActionItemProjectVO>({
    //   width: 120,
    // }),
  ];

  return (
    <div
      className={classnames('dbg-gr-action-items-tables', className, {
        // 'dbg-gr-action-items-tables-small': width !== undefined && width <= 1440,
      })}
    >
      <Table
        className="dbg-gr-action-items-tables__table"
        verticalTop
        records={loading ? [] : records}
        recordKey={getProjectActionItemKey}
        columns={columns}
        addons={addons}
      />
    </div>
  );
};

export default GRActionItemsTables;
